import React, { useEffect, useMemo, useState } from "react";
import { FaHistory, FaFileAlt, FaFileExcel, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./style.css";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../../js/api.js";
import Loader from "../../components/Loader/index.js";

const formatarDataHora = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const formatarCnpjCpf = (valor) => {
  if (!valor) return "";
  const nums = String(valor).replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return nums
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const TIPOS_AUTOR = [
  { value: "", label: "Todos os autores" },
  { value: "EMPRESA", label: "Empresa" },
  { value: "ESTUDANTE", label: "Estudante" },
  { value: "CIEE", label: "CIEE" },
  { value: "SISTEMA", label: "Sistema" },
];

// Catálogo oficial de eventos do Cadastro Empresa.
// O "value" deve bater com o que a API retorna no campo `evento`.
const TIPOS_EVENTO = [
  { value: "", label: "Todos os eventos" },
  {
    value: "INICIO_PREENCHIMENTO_EMPRESA",
    label: "Início do preenchimento da empresa",
  },
  {
    value: "DOCUMENTO_ANEXADO_EMPRESA",
    label: "Documento anexado pela empresa",
  },
  {
    value: "DOCUMENTO_SUBSTITUIDO_EMPRESA",
    label: "Documento substituído pela empresa",
  },
  {
    value: "DOCUMENTO_EXCLUIDO_EMPRESA",
    label: "Documento excluído pela empresa",
  },
  {
    value: "SOLICITACAO_FINALIZACAO_EMPRESA",
    label: "Solicitação de finalização pela empresa",
  },
  {
    value: "DADOS_DISPONIBILIZADOS_REVISAO_EMPRESA",
    label: "Dados disponibilizados para revisão da empresa",
  },
  {
    value: "CONFIRMACAO_DADOS_EMPRESA",
    label: "Confirmação dos dados pela empresa",
  },
  {
    value: "ESTUDANTE_HABILITADO_PREENCHIMENTO",
    label: "Estudante habilitado para preenchimento",
  },
  {
    value: "INICIO_PREENCHIMENTO_ESTUDANTE",
    label: "Início do preenchimento do estudante",
  },
  {
    value: "DOCUMENTO_ANEXADO_ESTUDANTE",
    label: "Documento anexado pelo estudante",
  },
  {
    value: "DOCUMENTO_SUBSTITUIDO_ESTUDANTE",
    label: "Documento substituído pelo estudante",
  },
  {
    value: "DOCUMENTO_EXCLUIDO_ESTUDANTE",
    label: "Documento excluído pelo estudante",
  },
  {
    value: "SOLICITACAO_FINALIZACAO_ESTUDANTE",
    label: "Solicitação de finalização pelo estudante",
  },
  {
    value: "DADOS_DISPONIBILIZADOS_REVISAO_ESTUDANTE",
    label: "Dados disponibilizados para revisão do estudante",
  },
  {
    value: "CONFIRMACAO_DADOS_ESTUDANTE",
    label: "Confirmação dos dados pelo estudante",
  },
  {
    value: "CADASTRO_DISPONIBILIZADO_VALIDACAO",
    label: "Cadastro disponibilizado para validação",
  },
  {
    value: "CADASTRO_ATRIBUIDO_OPERADOR",
    label: "Cadastro atribuído ao operador",
  },
  { value: "ALTERACAO_DADOS_CIEE", label: "Alteração de dados pelo CIEE" },
  {
    value: "DOCUMENTO_SUBSTITUIDO_CIEE",
    label: "Documento substituído pelo CIEE",
  },
  { value: "DOCUMENTO_EXCLUIDO_CIEE", label: "Documento excluído pelo CIEE" },
  { value: "CORRECAO_SOLICITADA", label: "Correção solicitada" },
  {
    value: "CADASTRO_DEVOLVIDO_CORRECAO",
    label: "Cadastro devolvido para correção",
  },
  { value: "CORRECAO_REALIZADA", label: "Correção realizada" },
  {
    value: "NOVA_CONFIRMACAO_APOS_CORRECAO",
    label: "Nova confirmação após correção",
  },
  {
    value: "CADASTRO_ATRIBUIDO_NOVAMENTE",
    label: "Cadastro atribuído novamente",
  },
  { value: "CADASTRO_VALIDADO", label: "Cadastro validado" },
  {
    value: "INCLUSAO_PENDENCIA_AUTOMATICA",
    label: "Inclusão de pendência automática",
  },
  {
    value: "RETIRADA_PENDENCIA_AUTOMATICA",
    label: "Retirada de pendência automática",
  },
];

const rotuloEvento = (valor) => {
  const encontrado = TIPOS_EVENTO.find((e) => e.value === valor);
  return encontrado ? encontrado.label : valor || "—";
};

// ============ Helpers para o campo "campo_alterado" ============
// O backend pode mandar o campo em formatos diferentes. Esta função entende
// todos eles e devolve um objeto canônico { coluna, posicao, tipo }.
//   - "salario"                                      (simples)
//   - "salario (col. 59)"                            (com posição da coluna)
//   - "59|salario"                                   (pipe)
//   - "salario|float"                                (pipe com tipo)
//   - { coluna: "salario", posicao: 59, tipo: "float" } (objeto)
const parsearCampoAlterado = (campo) => {
  if (campo === null || campo === undefined || campo === "") {
    return { coluna: "", posicao: null, tipo: "" };
  }

  if (typeof campo === "object") {
    return {
      coluna: campo.coluna || campo.nome || campo.campo || "",
      posicao: campo.posicao ?? campo.column_id ?? campo.ordinal ?? null,
      tipo: campo.tipo || campo.data_type || "",
    };
  }

  const texto = String(campo).trim();

  const mColPos = /^(.+?)\s*\(\s*col\.?\s*(\d+)\s*\)\s*$/i.exec(texto);
  if (mColPos) {
    return {
      coluna: mColPos[1].trim(),
      posicao: Number(mColPos[2]),
      tipo: "",
    };
  }

  if (texto.includes("|")) {
    const pedacos = texto.split("|").map((p) => p.trim());
    let posicao = null;
    let inicio = 0;
    if (/^\d+$/.test(pedacos[0])) {
      posicao = Number(pedacos[0]);
      inicio = 1;
    }
    const TIPOS_DADO = new Set([
      "int",
      "integer",
      "float",
      "double",
      "decimal",
      "varchar",
      "char",
      "text",
      "date",
      "datetime",
      "timestamp",
      "boolean",
      "bool",
    ]);
    let tipo = "";
    let fim = pedacos.length;
    if (TIPOS_DADO.has(pedacos[fim - 1].toLowerCase())) {
      tipo = pedacos[fim - 1];
      fim -= 1;
    }
    const coluna = pedacos.slice(inicio, fim).join("|");
    return { coluna, posicao, tipo };
  }

  return { coluna: texto, posicao: null, tipo: "" };
};

// Render amigável: "salario (col. 59)" se vier com posição; senão só o nome.
const formatarCampoAlterado = (campo) => {
  const { coluna, posicao } = parsearCampoAlterado(campo);
  if (!coluna) return "—";
  if (posicao) return `${coluna} (col. ${posicao})`;
  return coluna;
};

// ============ Detecção e formatação de tipos (texto/número/data/moeda) ============

const PALAVRAS_CHAVE_MOEDA = [
  "salario",
  "salário",
  "bolsa",
  "auxilio",
  "auxílio",
  "remuneracao",
  "remuneração",
  "valor_bolsa",
  "renda",
  "preco",
  "preço",
  "mensalidade",
  "custo",
];

const ehChaveMoeda = (campo) => {
  if (!campo) return false;
  const { coluna } = parsearCampoAlterado(campo);
  const normalizado = String(coluna || campo).toLowerCase().trim();
  return PALAVRAS_CHAVE_MOEDA.some((p) => normalizado.includes(p));
};

const parseDataSegura = (valor) => {
  if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
  if (typeof valor !== "string") return null;
  const s = valor.trim();
  if (!s) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(
    s,
  );
  if (iso) {
    const [, y, m, d, hh = "00", mm = "00", ss = "00"] = iso;
    const dt = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return isNaN(dt.getTime()) ? null : dt;
  }
  const br = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(
    s,
  );
  if (br) {
    const [, d, m, y, hh = "00", mm = "00", ss = "00"] = br;
    const dt = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
};

const pareceMoeda = (valor) =>
  typeof valor === "string" &&
  (/^\s*[R$€¥£]\s*[\d.,\s]+/.test(valor) || /[\d]+[.,]\d{2}\s*$/.test(valor));

const pareceNumero = (valor) => {
  if (typeof valor === "number") return Number.isFinite(valor);
  if (typeof valor !== "string") return false;
  const s = valor.trim();
  if (!s) return false;
  return /^-?\d{1,3}(?:[\.,]\d{3})*(?:[.,]\d+)?$|^-?\d+(?:[.,]\d+)?$/.test(s);
};

const paraNumero = (valor) => {
  if (typeof valor === "number") return valor;
  if (typeof valor !== "string") return NaN;
  let s = valor.trim().replace(/[R$€¥£\s]/g, "");
  if (!s) return NaN;
  if (/,\d{1,2}$/.test(s) && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (/,\d{1,2}$/.test(s)) {
    s = s.replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const detectarTipo = (valor, campoAlterado = "") => {
  if (valor === null || valor === undefined || valor === "") return "texto";
  if (campoAlterado && ehChaveMoeda(campoAlterado)) return "moeda";
  if (parseDataSegura(valor)) return "data";
  if (pareceMoeda(valor)) return "moeda";
  if (pareceNumero(valor)) {
    const n = paraNumero(valor);
    return Number.isFinite(n) ? "numero" : "texto";
  }
  return "texto";
};

const formatarNumeroTabela = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString("pt-BR", { maximumFractionDigits: 6 })
    : "—";

const formatarMoedaTabela = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

const formatarDataTabela = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const obterValorParaXlsx = (valor, campoAlterado = "") => {
  if (valor === null || valor === undefined || valor === "") {
    return { valor: "", formato: undefined };
  }
  const tipo = detectarTipo(valor, campoAlterado);
  if (tipo === "data") {
    return { valor: parseDataSegura(valor), formato: "dd/mm/yyyy hh:mm" };
  }
  if (tipo === "moeda") {
    return { valor: paraNumero(valor), formato: '"R$" #,##0.00' };
  }
  if (tipo === "numero") {
    return { valor: paraNumero(valor), formato: "#,##0.######" };
  }
  return { valor: String(valor), formato: undefined };
};

const HistoricoEventos = () => {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [itens, setItens] = useState([]);
  const [erroApi, setErroApi] = useState("");
  const [filtroCnpjCpf, setFiltroCnpjCpf] = useState("");
  const [filtroCnpjCpfAplicado, setFiltroCnpjCpfAplicado] = useState("");
  const [filtroAutor, setFiltroAutor] = useState("");
  const [filtroAutorAplicado, setFiltroAutorAplicado] = useState("");
  const [filtroEvento, setFiltroEvento] = useState("");
  const [filtroEventoAplicado, setFiltroEventoAplicado] = useState("");
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);

  const buscarEventos = async (cnpjCpf = "", autor = "", evento = "") => {
    setCarregando(true);
    try {
      const token = localStorage.getItem("token");
      const cnpjNumeros = String(cnpjCpf).replace(/\D/g, "");

      const params = new URLSearchParams();
      if (cnpjNumeros) params.append("cpf_cnpj", cnpjNumeros);
      if (autor) params.append("autor", autor);
      if (evento) params.append("evento", evento);

      const url = `${API_BASE_URL}/historico-eventos${params.toString() ? `?${params.toString()}` : ""}`;

      try {
        const resp = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // A API pode responder com:
        //  - array simples: [...]
        //  - paginado: { total, page, limit, pages, data: [...] }
        //  - erro: { errors: ["..."] } (HTTP 200 mesmo assim)
        const payload = resp.data;
        let lista = [];
        if (Array.isArray(payload)) {
          lista = payload;
        } else if (Array.isArray(payload?.data)) {
          lista = payload.data;
        } else if (Array.isArray(payload?.rows)) {
          // Sequelize findAndCountAll devolve { count, rows }
          lista = payload.rows;
        } else if (payload?.errors) {
          setErroApi(payload.errors.join(" • "));
        }
        setItens(lista);
        return;
      } catch (err) {
        if (err?.response?.status !== 404 && err?.response?.status !== 405) {
          throw err;
        }
      }

      setItens([]);
    } catch (err) {
      setItens([]);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    buscarEventos();
  }, []);

  const aplicarFiltro = () => {
    setFiltroCnpjCpfAplicado(filtroCnpjCpf.trim());
    setFiltroAutorAplicado(filtroAutor);
    setFiltroEventoAplicado(filtroEvento);
    setFiltrosAplicados(true);
    setErroApi("");
    buscarEventos(filtroCnpjCpf.trim(), filtroAutor, filtroEvento);
  };

  const limparFiltro = () => {
    setFiltroCnpjCpf("");
    setFiltroCnpjCpfAplicado("");
    setFiltroAutor("");
    setFiltroAutorAplicado("");
    setFiltroEvento("");
    setFiltroEventoAplicado("");
    setFiltrosAplicados(false);
    buscarEventos("", "", "");
  };

  const itensFiltrados = useMemo(() => {
    let lista = itens;

    const cnpjAlvo = filtroCnpjCpfAplicado.replace(/\D/g, "");
    if (cnpjAlvo) {
      lista = lista.filter((item) => {
        const valorItem = String(item.cpf_cnpj || item.cnpj_cpf || "").replace(
          /\D/g,
          "",
        );
        return valorItem.includes(cnpjAlvo);
      });
    }

    if (filtroAutorAplicado) {
      lista = lista.filter(
        (item) =>
          String(item.autor || "").toUpperCase() ===
          filtroAutorAplicado.toUpperCase(),
      );
    }

    if (filtroEventoAplicado) {
      lista = lista.filter((item) => item.evento === filtroEventoAplicado);
    }

    return lista;
  }, [itens, filtroCnpjCpfAplicado, filtroAutorAplicado, filtroEventoAplicado]);

  const temFiltroAplicado =
    filtrosAplicados &&
    (filtroCnpjCpfAplicado !== "" ||
      filtroAutorAplicado !== "" ||
      filtroEventoAplicado !== "");

  const obterDataHora = (item) =>
    item.data ||
    item.data_hora ||
    item.dataHora ||
    item.created_at ||
    item.createdAt ||
    item.timestamp ||
    item.updated_at ||
    null;

  const obterCnpjCpf = (item) =>
    item.cpf_cnpj || item.cnpj_cpf || item.cpfCnpj || "";

  const obterUsuario = (item) =>
    item.usuario_nome ||
    item.usuario ||
    item.nome_usuario ||
    item.nome ||
    "";

  // Monta a planilha em memória. Cada chave do objeto vira uma coluna,
  // e a biblioteca xlsx cuida de tudo: tipos, datas, acentos, células.
  const exportarXlsx = async () => {
    try {
      const cabecalho = [
        "Nome do Usuário",
        "CPF ou CNPJ",
        "Data/Hora",
        "Campo Alterado",
        "Valor Anterior",
        "Valor Novo",
        "Autor",
        "Atividade",
        "Status",
      ];

      const linhas = itensFiltrados.map((item) => {
        const anterior = obterValorParaXlsx(item.valor_anterior, item.campo_alterado);
        const novo = obterValorParaXlsx(item.valor_novo, item.campo_alterado);
        return {
          "Nome do Usuário": obterUsuario(item),
          "CPF ou CNPJ": formatarCnpjCpf(obterCnpjCpf(item)),
          "Data/Hora": obterDataHora(item),
          "Campo Alterado": formatarCampoAlterado(item.campo_alterado),
          "Valor Anterior": anterior.valor,
          "Valor Novo": novo.valor,
          Autor: item.autor || "",
          Atividade: item.atividade || rotuloEvento(item.evento),
          Status: item.status ?? "",
        };
      });

      const planilha = XLSX.utils.json_to_sheet(linhas, { header: cabecalho });

      // Aplica formato nativo do Excel nas colunas dinâmicas.
      const colValorAnterior = cabecalho.indexOf("Valor Anterior");
      const colValorNovo = cabecalho.indexOf("Valor Novo");
      const colDataHora = cabecalho.indexOf("Data/Hora");

      itensFiltrados.forEach((item, rowIdx) => {
        const r = rowIdx + 1; // +1 porque a linha 0 é o cabeçalho
        if (colValorAnterior >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colValorAnterior, r });
          const fmt = obterValorParaXlsx(item.valor_anterior, item.campo_alterado).formato;
          if (planilha[ref] && fmt) planilha[ref].z = fmt;
        }
        if (colValorNovo >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colValorNovo, r });
          const fmt = obterValorParaXlsx(item.valor_novo, item.campo_alterado).formato;
          if (planilha[ref] && fmt) planilha[ref].z = fmt;
        }
        if (colDataHora >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colDataHora, r });
          const dh = obterDataHora(item);
          if (planilha[ref] && dh) {
            const d = new Date(dh);
            if (!isNaN(d.getTime())) {
              planilha[ref].t = "d";
              planilha[ref].v = d;
              planilha[ref].z = "dd/mm/yyyy hh:mm";
            }
          }
        }
      });

      // Largura automática das colunas (em caracteres).
      planilha["!cols"] = [
        { wch: 30 }, // Nome do Usuário
        { wch: 18 }, // CPF ou CNPJ
        { wch: 20 }, // Data/Hora
        { wch: 28 }, // Campo Alterado
        { wch: 30 }, // Valor Anterior
        { wch: 30 }, // Valor Novo
        { wch: 14 }, // Autor
        { wch: 40 }, // Atividade
        { wch: 12 }, // Status
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, planilha, "Histórico de Eventos");

      XLSX.writeFile(
        workbook,
        `historico-eventos_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (err) {
      // Falha ao exportar XLSX: silenciosa para o usuário final.
    }
  };

  return (
    <main className="container-historico-eventos">
      <div className="historico-topbar">
        <button
          type="button"
          className="historico-btn-voltar"
          onClick={() => navigate("/dashboard")}
        >
          <FaArrowLeft /> Voltar
        </button>
      </div>
      <header className="historico-header">
        <div className="historico-titulo-bloco">
          <h1>
            <FaHistory /> Histórico de Registros
          </h1>
          <p className="historico-subtitulo">
            Rastreamento de ações realizadas no sistema.
          </p>
        </div>

        <div className="historico-acoes-topo">
          <button
            type="button"
            className="historico-botao historico-botao-primario"
            onClick={exportarXlsx}
            disabled={carregando}
          >
            <FaFileExcel /> Exportar Documento
          </button>
        </div>
      </header>

      <section className="historico-filtros">
        <div className="historico-filtro-linha">
          <div className="historico-filtro-grupo historico-filtro-grupo--cresce">
            <label htmlFor="filtro-evento-cnpj-cpf">
              FILTRAR POR EMPRESA (CPF/CNPJ)
            </label>
            <input
              id="filtro-evento-cnpj-cpf"
              type="text"
              placeholder="00.000.000/0000-00"
              value={filtroCnpjCpf}
              onChange={(e) => setFiltroCnpjCpf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") aplicarFiltro();
              }}
            />
          </div>

          <div className="historico-filtro-grupo">
            <label htmlFor="filtro-evento-autor">AUTOR</label>
            <select
              id="filtro-evento-autor"
              value={filtroAutor}
              onChange={(e) => setFiltroAutor(e.target.value)}
            >
              <option value="" disabled>
                Selecione
              </option>
              {TIPOS_AUTOR.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <div className="historico-filtro-grupo historico-filtro-grupo--cresce">
            <label htmlFor="filtro-evento-tipo">EVENTO</label>
            <select
              id="filtro-evento-tipo"
              value={filtroEvento}
              onChange={(e) => setFiltroEvento(e.target.value)}
            >
              <option value="" disabled>
                Selecione
              </option>
              {TIPOS_EVENTO.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="historico-filtrar"
            onClick={aplicarFiltro}
            disabled={carregando}
          >
            Filtrar
          </button>
          <button
            type="button"
            className="historico-limpar-filtros"
            onClick={limparFiltro}
            disabled={carregando}
          >
            Limpar
          </button>
        </div>
      </section>

      <section className="historico-informacoes">
        <div className="historico-informacoes-header">
          <FaHistory />
          <div>
            <h2>Informações do Histórico</h2>
            <p>
              O histórico registra as ações realizadas no cadastro e identifica
              o responsável, a data e o evento executado.
            </p>
          </div>
        </div>
        <div className="historico-informacoes-grid">
          <div className="historico-informacao-item">
            <strong>Usuário responsável</strong>
            <span>Identifica o usuário que realizou a ação no sistema.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Data e hora</strong>
            <span>Registra exatamente quando o evento foi realizado.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Evento realizado</strong>
            <span>Identifica a ação executada no cadastro.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Autor</strong>
            <span>
              Identifica se a ação foi realizada pela empresa, estudante, CIEE
              ou sistema.
            </span>
          </div>
          <div className="historico-informacao-item">
            <strong>CPF ou CNPJ</strong>
            <span>Permite identificar o cadastro relacionado ao evento.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Status</strong>
            <span>Apresenta o status relacionado ao evento registrado.</span>
          </div>
        </div>
      </section>

      <section className="historico-resultados">
        <p className="historico-resultados-titulo">REGISTROS ENCONTRADOS</p>
        {carregando ? (
          <div className="historico-loader">
            <Loader />
          </div>
        ) : itensFiltrados.length === 0 ? (
          <div className="historico-vazio">
            <FaFileAlt size={48} />
            {erroApi ? (
              <p className="historico-erro-api">⚠️ {erroApi}</p>
            ) : temFiltroAplicado ? (
              <p>Nenhum registro encontrado.</p>
            ) : (
              <p>
                Use os filtros acima e clique em <strong>Filtrar</strong> para
                buscar os registros.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="historico-tabela-wrapper">
              <table className="historico-tabela">
                <thead>
                  <tr>
                    <th>Nome do Usuário</th>
                    <th>CPF ou CNPJ</th>
                    <th>Data/Hora</th>
                    <th>Campo Alterado</th>
                    <th>Valor Anterior</th>
                    <th>Valor Novo</th>
                    <th>Autor</th>
                    <th>Atividade</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {itensFiltrados.map((item) => (
                    <tr
                      key={
                        item.id ??
                        `${item.cpf_cnpj || item.cnpj_cpf}-${item.data_hora || item.data || item.created_at}-${item.evento}`
                      }
                    >
                      <td>
                        {item.usuario_nome ||
                          item.usuario ||
                          item.nome_usuario ||
                          "—"}
                      </td>
                      <td>
                        {formatarCnpjCpf(item.cpf_cnpj || item.cnpj_cpf) || "—"}
                      </td>
                      <td>
                        {formatarDataHora(
                          item.data_hora || item.data || item.created_at,
                        )}
                      </td>
                      <td>{item.campo_alterado ?? "—"}</td>
                      <td>{item.valor_anterior ?? "—"}</td>
                      <td>{item.valor_novo ?? "—"}</td>
                      <td>
                        <span
                          className={`badge-autor badge-autor-${(item.autor || "").toLowerCase()}`}
                        >
                          {item.autor || "—"}
                        </span>
                      </td>
                      <td>{item.atividade || rotuloEvento(item.evento)}</td>
                      <td>
                        {item.status !== undefined && item.status !== null ? (
                          <span
                            className={`historico-badge-status historico-badge-status-${String(item.status).toLowerCase().replace(/\s/g, "-")}`}
                          >
                            {item.status}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <footer className="historico-rodape">
              <strong>REGISTROS ENCONTRADOS</strong>
              <span>
                Total: <strong>{itensFiltrados.length}</strong> registro(s)
              </span>
            </footer>
          </>
        )}
      </section>
    </main>
  );
};

export default HistoricoEventos;
